import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import VideoPlayer from './VideoPlayer';

/**
 * A project-level carousel that swaps video + headline + description together.
 * Unified prev/next bar sits above the video on all viewports.
 */
export default function ProjectCarousel({ projects, ctaLabel, ctaHref }) {
  const [index, setIndex] = useState(0);
  const project = projects[index];

  const goPrev = () => setIndex((i) => (i === 0 ? projects.length - 1 : i - 1));
  const goNext = () => setIndex((i) => (i === projects.length - 1 ? 0 : i + 1));

  return (
    <div data-testid="project-carousel">
      {/* Prev/next bar above the video (all viewports) */}
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-[#27272A]">
        <button
          type="button"
          onClick={goPrev}
          aria-label="Previous project"
          data-testid="project-carousel-prev"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-white transition-colors group"
        >
          <div className="w-11 h-11 md:w-12 md:h-12 rounded-full border border-[#27272A] flex items-center justify-center group-hover:bg-[#007AFF] group-hover:border-[#007AFF] transition-all">
            <ChevronLeft size={20} />
          </div>
          <span className="text-xs uppercase tracking-widest hidden sm:inline">Prev</span>
        </button>

        <span className="text-[#007AFF] text-sm md:text-base font-mono tabular-nums">
          {String(index + 1).padStart(2, '0')} / {String(projects.length).padStart(2, '0')}
        </span>

        <button
          type="button"
          onClick={goNext}
          aria-label="Next project"
          data-testid="project-carousel-next"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-white transition-colors group"
        >
          <span className="text-xs uppercase tracking-widest hidden sm:inline">Next</span>
          <div className="w-11 h-11 md:w-12 md:h-12 rounded-full border border-[#27272A] flex items-center justify-center group-hover:bg-[#007AFF] group-hover:border-[#007AFF] transition-all">
            <ChevronRight size={20} />
          </div>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Video */}
        <div>
          <AnimatePresence mode="wait">
            <motion.div
              key={`video-${project.id}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              <VideoPlayer
                vimeoId={project.vimeoId}
                vimeoHash={project.vimeoHash}
                posterImage={project.image}
                title={project.title}
                className="rounded-sm"
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Copy */}
        <div>
          <AnimatePresence mode="wait">
            <motion.div
              key={`copy-${project.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
            >
              <span className="caption" data-testid="project-carousel-tag">{project.tag}</span>
              <h3
                className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-4 mb-5"
                data-testid="project-carousel-title"
              >
                {project.title}
              </h3>
              <p
                className="text-[#A1A1AA] text-lg leading-relaxed mb-6"
                data-testid="project-carousel-description"
              >
                {project.description}
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                {project.services.map((service) => (
                  <span
                    key={service}
                    className="px-3 py-1 text-sm border border-[#27272A] text-[#A1A1AA]"
                  >
                    {service}
                  </span>
                ))}
              </div>
              {ctaHref && ctaLabel && (
                <a
                  href={ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid={`project-carousel-cta-${project.id}`}
                >
                  {ctaLabel}
                  <ArrowRight size={16} />
                </a>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
